/**
 * Area `website-reading` of the console gateway — over `apps/api/src/routes/website-reading/` (design §30.4.1). Owned by
 * plan-018 unit **API-AG** (ADR 0059): the website reading a version was built from, one read page's text, and "Read the
 * website again".
 *
 *   readWebsiteReading   the reading of the live version (or a named one): pages read and skipped with their reasons;
 *                        `null` when the version was built without a website (`404 website_reading_not_found`)
 *   readWebsitePage      one read page's text, from the reading's snapshot
 *   readWebsiteAgain     a draft of the live version with a fresh reading, its build started; the API's refusals
 *                        (`409 website_reading_in_progress`, `422 website_address_missing`, `429
 *                        agent_build_limit_reached`) reach the screen as the API worded them
 *   readAgentVersionInformation
 *                        what one version answers from — `getAgentVersion`'s `information` block (passage count, its
 *                        website reading, its inputs); `null` for a version of any template that has no information
 *                        index. Added by plan-018 WAPP-B, the screen that reads it (design §30.4.2).
 *
 * plan-018 REG spread this slice once into `../api-gateway.ts` (and its sandbox, `../sandbox/website-reading.ts`, into
 * `../sandbox-gateway.ts`). The register is EMPTY: all three operations are served.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readNullableText } from "../conversation-view.js";
import { MalformedResponseError, readArray, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** `WebsiteReadingView.outcome`; `null` only while the reading runs. */
export type WebsiteReadingOutcomeView = "read" | "partial" | "failed";

/** One page the reading read. */
export interface WebsiteReadPageView {
  readonly ordinal: number;
  readonly finalUrl: string;
  readonly title: string | null;
  readonly charCount: number;
}

/** One page the reading left out, and why (the skip reason, as the API names it). */
export interface WebsiteSkippedPageView {
  readonly url: string;
  readonly reason: string;
}

/** The website reading of one version. */
export interface WebsiteReadingView {
  readonly websiteReadingId: string;
  readonly agentVersionId: string;
  readonly startUrl: string;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly outcome: WebsiteReadingOutcomeView | null;
  /** Why a failed reading failed (the API's closed list); `null` otherwise. */
  readonly failureCode: string | null;
  readonly pageReadCount: number;
  readonly pageSkippedCount: number;
  readonly strippedCharacterCount: number;
  readonly readPage: readonly WebsiteReadPageView[];
  readonly skippedPage: readonly WebsiteSkippedPageView[];
}

/** One read page and the text the build kept from it. */
export interface WebsitePageView {
  readonly ordinal: number;
  readonly finalUrl: string;
  readonly title: string | null;
  readonly text: string;
}

/**
 * What one version answers from (`AgentVersionInformation`, design §30.4.2): how many passages its information index
 * holds, the website reading it was built from (or `null`), and its inputs — the pasted text's document, the
 * documents, the website address.
 */
export interface AgentVersionInformationView {
  readonly passageCount: number;
  readonly websiteReadingId: string | null;
  readonly pastedTextDocumentId: string | null;
  readonly documentId: readonly string[];
  readonly websiteAddress: string | null;
}

/** The draft "Read the website again" opened, and its build. */
export interface WebsiteRereadView {
  readonly voiceAgentId: string;
  readonly agentVersionId: string;
  readonly agentVersionLabel: string;
  readonly baseAgentVersionId: string;
  readonly buildJobId: string;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-018 API-AG: the reading the live version (or `agentVersionId`) was built from; `null` without a website. */
    readWebsiteReading(voiceAgentId: string, agentVersionId: string | null): Promise<WebsiteReadingView | null>;
    /** plan-018 API-AG: one read page's text. */
    readWebsitePage(voiceAgentId: string, ordinal: number, agentVersionId: string | null): Promise<WebsitePageView>;
    /** plan-018 API-AG: "Read the website again" — a draft of the live version with a fresh reading. */
    readWebsiteAgain(voiceAgentId: string): Promise<WebsiteRereadView>;
    /** plan-018 WAPP-B: what one version answers from; `null` for a version without an information index. */
    readAgentVersionInformation(voiceAgentId: string, agentVersionId: string): Promise<AgentVersionInformationView | null>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type WebsiteReadingGatewaySlice = Pick<
  ConsoleGateway,
  "readWebsiteReading" | "readWebsitePage" | "readWebsiteAgain" | "readAgentVersionInformation"
>;

/** This area's rows of `GATEWAY_GAP`: none — every operation is served. */
export const websiteReadingGap: readonly GatewayGap[] = [];

const OUTCOME: readonly WebsiteReadingOutcomeView[] = ["read", "partial", "failed"];

function nullableOutcome(value: unknown, path: string): WebsiteReadingOutcomeView | null {
  if (value === null) return null;
  const raw = readText(value, path);
  const found = OUTCOME.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a reading outcome", raw);
  return found;
}

/** One `WebsiteReadingView`. */
export function websiteReadingOf(wire: unknown, path: string): WebsiteReadingView {
  const row = readRecord(wire, path);
  return {
    websiteReadingId: readText(row["website_reading_id"], `${path}.website_reading_id`),
    agentVersionId: readText(row["agent_version_id"], `${path}.agent_version_id`),
    startUrl: readText(row["start_url"], `${path}.start_url`),
    startedAt: readText(row["started_at"], `${path}.started_at`),
    finishedAt: readNullableText(row["finished_at"], `${path}.finished_at`),
    outcome: nullableOutcome(row["outcome"], `${path}.outcome`),
    failureCode: readNullableText(row["failure_code"], `${path}.failure_code`),
    pageReadCount: readWholeNumber(row["page_read_count"], `${path}.page_read_count`),
    pageSkippedCount: readWholeNumber(row["page_skipped_count"], `${path}.page_skipped_count`),
    strippedCharacterCount: readWholeNumber(row["stripped_character_count"], `${path}.stripped_character_count`),
    readPage: readArray(row["read_page"], `${path}.read_page`).map((one, index) => {
      const at = `${path}.read_page[${String(index)}]`;
      const page = readRecord(one, at);
      return {
        ordinal: readWholeNumber(page["ordinal"], `${at}.ordinal`),
        finalUrl: readText(page["final_url"], `${at}.final_url`),
        title: readNullableText(page["title"], `${at}.title`),
        charCount: readWholeNumber(page["char_count"], `${at}.char_count`),
      };
    }),
    skippedPage: readArray(row["skipped_page"], `${path}.skipped_page`).map((one, index) => {
      const at = `${path}.skipped_page[${String(index)}]`;
      const page = readRecord(one, at);
      return { url: readText(page["url"], `${at}.url`), reason: readText(page["reason"], `${at}.reason`) };
    }),
  };
}

/** One `WebsitePageText`. */
export function websitePageOf(wire: unknown, path: string): WebsitePageView {
  const row = readRecord(wire, path);
  return {
    ordinal: readWholeNumber(row["ordinal"], `${path}.ordinal`),
    finalUrl: readText(row["final_url"], `${path}.final_url`),
    title: readNullableText(row["title"], `${path}.title`),
    text: readText(row["text"], `${path}.text`),
  };
}

/** One `WebsiteRereadStarted`. */
export function websiteRereadOf(wire: unknown, path: string): WebsiteRereadView {
  const row = readRecord(wire, path);
  return {
    voiceAgentId: readText(row["voice_agent_id"], `${path}.voice_agent_id`),
    agentVersionId: readText(row["agent_version_id"], `${path}.agent_version_id`),
    agentVersionLabel: readText(row["agent_version_label"], `${path}.agent_version_label`),
    baseAgentVersionId: readText(row["base_agent_version_id"], `${path}.base_agent_version_id`),
    buildJobId: readText(row["build_job_id"], `${path}.build_job_id`),
  };
}

/** `AgentVersionDetail.information`: `null` for a version of a template without an information index. */
export function agentVersionInformationOf(wire: unknown, path: string): AgentVersionInformationView | null {
  if (wire === null) return null;
  const row = readRecord(wire, path);
  const input = readRecord(row["input_summary"], `${path}.input_summary`);
  return {
    passageCount: readWholeNumber(row["passage_count"], `${path}.passage_count`),
    websiteReadingId: readNullableText(row["website_reading_id"], `${path}.website_reading_id`),
    pastedTextDocumentId: readNullableText(input["pasted_text_document_id"], `${path}.input_summary.pasted_text_document_id`),
    documentId: readArray(input["document_id"], `${path}.input_summary.document_id`).map((one, index) =>
      readText(one, `${path}.input_summary.document_id[${String(index)}]`),
    ),
    websiteAddress: readNullableText(input["website_address"], `${path}.input_summary.website_address`),
  };
}

/** Whether the API answered `404 website_reading_not_found` — the version was built without a website. */
function builtWithoutWebsite(result: ApiResult): boolean {
  if (result.status !== 404) return false;
  const envelope = (result.body as { readonly error?: { readonly code?: unknown } } | null)?.error;
  return envelope?.code === "website_reading_not_found";
}

export function websiteReadingApiSlice(client: WeTalkApiClient, _context: SliceContext): WebsiteReadingGatewaySlice {
  const dataOf = (body: unknown, operationId: string): unknown => readRecord(body, operationId)["data"];
  const queryOf = (agentVersionId: string | null) => (agentVersionId === null ? {} : { query: { agent_version_id: agentVersionId } });
  return {
    async readWebsiteReading(voiceAgentId, agentVersionId) {
      const result = await client.getWebsiteReading({ path: { voice_agent_id: voiceAgentId }, ...queryOf(agentVersionId) });
      if (builtWithoutWebsite(result)) return null;
      return websiteReadingOf(dataOf(assertOk(result), "getWebsiteReading"), "getWebsiteReading.data");
    },
    async readWebsitePage(voiceAgentId, ordinal, agentVersionId) {
      const result = await client.getWebsiteReadingPage({
        path: { voice_agent_id: voiceAgentId, ordinal: String(ordinal) },
        ...queryOf(agentVersionId),
      });
      return websitePageOf(dataOf(assertOk(result), "getWebsiteReadingPage"), "getWebsiteReadingPage.data");
    },
    async readWebsiteAgain(voiceAgentId) {
      const result = await client.readWebsiteAgain({
        path: { voice_agent_id: voiceAgentId },
        header: { "idempotency-key": crypto.randomUUID() },
      });
      return websiteRereadOf(dataOf(assertOk(result), "readWebsiteAgain"), "readWebsiteAgain.data");
    },
    async readAgentVersionInformation(voiceAgentId, agentVersionId) {
      const result = await client.getAgentVersion({ path: { voice_agent_id: voiceAgentId, agent_version_id: agentVersionId } });
      const data = readRecord(dataOf(assertOk(result), "getAgentVersion"), "getAgentVersion.data");
      return agentVersionInformationOf(data["information"], "getAgentVersion.data.information");
    },
  };
}
